import { Component, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../auth/services/auth.service';
import { UserRole } from '../../../auth/models/user-roles';

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  route: string;
  roles: UserRole[];
}

@Component({
  selector: 'app-admin-dashboard-page',
  templateUrl: './admin-dashboard-page.component.html',
  standalone: false
})
export class AdminDashboardPageComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  quickActions: QuickAction[] = [
    /*{
      title: 'Manage Tenants',
      description: 'View and manage tenant organizations',
      icon: 'business',
      route: 'tenants',
      roles: [UserRole.SuperUser]
    },*/
    {
      title: 'Locker Banks',
      description: 'Configure locker banks and locations',
      icon: 'location_city',
      route: 'locker-banks-and-lockers',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    },
    {
      title: 'Staff & Credentials',
      description: 'Manage card holders and access credentials',
      icon: 'badge',
      route: 'card-holders-and-credentials',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    },
    {
      title: 'Locker Statuses',
      description: 'View locker status and audit information',
      icon: 'storage',
      route: 'locker-statuses-and-audits',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    },
    {
      title: 'Site Locker Controllers',
      description: 'Manage SLC devices and configurations',
      icon: 'devices',
      route: 'slcs',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    },
    {
      title: 'Kiosks',
      description: 'Manage Kiosks and configurations',
      icon: 'desktop_windows',
      route: 'kiosk-groups',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    },
    {
      title: 'System Metrics',
      description: 'View system performance and analytics',
      icon: 'dashboard',
      route: 'metrics',
      roles: [UserRole.SuperUser, UserRole.TenantAdmin]
    }
  ];

  get visibleActions(): QuickAction[] {
    const userData = this.authService.userData;
    if (!userData) return [];

    return this.quickActions
      .filter(action =>
        action.roles.some(role =>
          userData.roles?.includes(role) &&
          this.authService.isRoleEnabled(role)
        )
      )
      .map(action => {
        // Adjust Locker Statuses route based on role
        if (action.title === 'Locker Statuses') {
          const isSuperUser = userData.roles?.includes(UserRole.SuperUser) && 
                             this.authService.isRoleEnabled(UserRole.SuperUser);
          return {
            ...action,
            route: isSuperUser ? 'locker-statuses-and-audits' : 'locker-statuses'
          };
        }
        return action;
      });
  }

  get userName(): string {
    return this.authService.userData?.name ?? 'User';
  }

  get userRole(): string {
    const userData = this.authService.userData;
    if (!userData?.roles?.length) return '';
    
    if (userData.roles.includes(UserRole.SuperUser)) return 'Super User';
    if (userData.roles.includes(UserRole.TenantAdmin)) return 'Tenant Admin';
    if (userData.roles.includes(UserRole.LockerBankAdmin)) return 'Locker Bank Admin';
    if (userData.roles.includes(UserRole.Installer)) return 'Installer';
    
    return '';
  }

  navigate(route: string): void {
    this.router.navigate([route], { relativeTo: this.route });
  }
}
